<template>
  <dara-single-content-layout container-size="lg">
    <template #content>
      <dara-header>{{ $t('search') }}</dara-header>
      <div class="mb--xl">
        <dara-input
          v-model="localSearchTerm"
          :focus="true"
          :placeholder="$t('search-for-articles-and-more')"
          @keyup.enter="search()" />
      </div>

      <el-tabs v-model="tabActive" @tab-click="search()">
        <el-tab-pane :label="$t('articles')" name="articles">
          <div v-if="hasArticles" class="mb--xl">
            <article v-for="article in articleList" :key="article.id" class="mb--xl">
              <dara-article-card :article="article" />
            </article>
          </div>
          <el-empty v-else :description="emptyMessage"></el-empty>
          <dara-pagination
            :page.sync="articlesPagination.page"
            :total="articlesPagination.total"
            :page-size="articlesPagination.limit"
            @current-change="search" />
        </el-tab-pane>

        <el-tab-pane :label="$t('users')" name="users">
          <div v-if="hasUsers" class="mb--xl">
            <div v-for="user in userList" :key="user.id" class="mb--xl">
              <dara-user-card
                :user="user"
                :best-writer-id="bestWriter ? bestWriter.id : ''"
                :top-writer-id="topWriter ? topWriter.id : ''"
                :top-reader-id="topReader ? topReader.id : ''"
                @setDeleteUser="setDeleteUser($event)" />
            </div>
            <dara-modal-delete
              v-if="showModal()"
              :content="$t('are-you-sure-you-want-to-delete-this-user')"
              @close="setDeleteUser(null)"
              @confirm="onDeleteUser()">
            </dara-modal-delete>
          </div>
          <el-empty v-else :description="emptyMessage"></el-empty>
          <dara-pagination
            :page.sync="usersPagination.page"
            :total="usersPagination.total"
            :page-size="usersPagination.limit"
            @current-change="search" />
        </el-tab-pane>

        <el-tab-pane :label="$t('tracks')" name="tracks">
          <div v-if="hasTracks" class="mb--xl">
            <div v-for="track in trackList" :key="track.id" class="mb-4">
              <dara-track-card :track="track" />
            </div>
          </div>
          <el-empty v-else :description="emptyMessage"></el-empty>
          <dara-pagination
            :page.sync="tracksPagination.page"
            :total="tracksPagination.total"
            :page-size="tracksPagination.limit"
            @current-change="search" />
        </el-tab-pane>

        <el-tab-pane :label="$t('questions')" name="questions">
          <div v-if="hasQuestions" class="mb--xl">
            <div v-for="question in questionList" :key="question.id" class="mb-4">
              <dara-question-card :question="question" />
            </div>
          </div>
          <el-empty v-else :description="emptyMessage"></el-empty>
          <dara-pagination
            :page.sync="questionsPagination.page"
            :total="questionsPagination.total"
            :page-size="questionsPagination.limit"
            @current-change="search" />
        </el-tab-pane>
      </el-tabs>
    </template>
  </dara-single-content-layout>
</template>

<script>
import { toastError } from '@/services/toastService';
import { searchArticles } from '@/modules/article/article.service';
import { searchTracks } from '@/modules/track/track.service';
import { searchQuestions } from '@/modules/question/question.service';
import { removeUser, searchUsers, fetchBestWriters, fetchTopReaders, fetchTopWriters } from '@/modules/user/user.service';
import { translate } from '@/services/translate.service';

import DaraArticleCard from '@/modules/article/components/ArticleCard.vue';
import DaraUserCard from '@/modules/user/components/UserCard.vue';
import DaraTrackCard from '@/modules/track/components/TrackCard.vue';
import DaraQuestionCard from '@/modules/question/components/QuestionCard.vue';
import DaraSingleContentLayout from '@/layouts/SingleContentLayout.vue';

export default {
  name: 'MySpaceSearchPage',
  components: {
    DaraArticleCard,
    DaraTrackCard,
    DaraQuestionCard,
    DaraUserCard,
    DaraSingleContentLayout,
  },
  data() {
    return {
      articleList: null,
      trackList: null,
      questionList: null,
      userList: null,
      articlesPagination: {
        page: 1,
        limit: 5,
      },
      tracksPagination: {
        page: 1,
        limit: 5,
      },
      questionsPagination: {
        page: 1,
        limit: 5,
      },
      usersPagination: {
        page: 1,
        limit: 5,
      },
      tabActive: 'articles',
      localSearchTerm: null,
      checked: false,
      bestWriter: null,
      topWriter: null,
      topReader: null,
      deletedUser: null,
    };
  },
  computed: {
    searchTerm() {
      return this.$route.query.term;
    },
    hasArticles() {
      return this.articleList?.length;
    },
    hasTracks() {
      return this.trackList?.length;
    },
    hasQuestions() {
      return this.questionList?.length;
    },
    hasUsers() {
      return this.userList?.length;
    },
    emptyMessage() {
      if (this.tabActive === 'articles') return `${translate('no-articles-found-for-term')} "${this.localSearchTerm}"`;
      if (this.tabActive === 'users') return `${translate('no-users-found-for-term')} "${this.localSearchTerm}"`;
      if (this.tabActive === 'tracks') return `${translate('no-tracks-found-for-term')} "${this.localSearchTerm}"`;
      return `${translate('no-questions-found-for-term')} "${this.localSearchTerm}"`;
    },
  },
  mounted() {
    this.localSearchTerm = this.searchTerm;
    this.fetchFeaturedUsers();
    this.search();
  },
  methods: {
    fetchFeaturedUsers() {
      this.fetchBestWriter();
      this.fetchTopWriter();
      this.fetchTopReader();
    },
    fetchBestWriter() {
      fetchBestWriters(1)
        .then(user => {
          this.bestWriter = user;
        })
        .catch(() => {
          toastError(`${translate('error-getting-best-writer')}`);
          this.bestWriter = null;
        });
    },
    fetchTopWriter() {
      fetchTopWriters(1)
        .then(user => {
          this.topWriter = user;
        })
        .catch(() => {
          toastError(`${translate('error-getting-top-writer')}`);
          this.topWriter = null;
        });
    },
    fetchTopReader() {
      fetchTopReaders(1)
        .then(user => {
          this.topReader = user;
        })
        .catch(() => {
          toastError(`${translate('error-getting-top-reader')}`);
          this.topReader = null;
        });
    },
    search() {
      if (!this.localSearchTerm) return;

      if (this.tabActive === 'articles') {
        this.searchArticles();
      } else if (this.tabActive === 'users') {
        this.searchUsers();
      } else if (this.tabActive === 'tracks') {
        this.searchTracks();
      } else if (this.tabActive === 'questions') {
        this.searchQuestions();
      }
    },
    searchArticles() {
      searchArticles(this.localSearchTerm, this.articlesPagination)
        .then(({ data, pagination }) => {
          this.articleList = data;
          Object.assign(this.articlesPagination, pagination);
          if (this.articleList.length) this.checked = true;
          if (!this.articleList.length && !this.checked) {
            this.tabActive = 'users';
            this.search();
          }
        })
        .catch(() => toastError(`${translate('error-on-searching-articles')}`));
    },
    searchUsers() {
      searchUsers(this.localSearchTerm, this.usersPagination)
        .then(({ data }) => {
          this.userList = data.users;
          this.$set(this.usersPagination, 'total', parseInt(data.total_searched_users));
          if (this.userList.length) this.checked = true;
          if (!this.userList.length && !this.checked) {
            this.tabActive = 'tracks';
            this.search();
          }
        })
        .catch(() => toastError(`${translate('error-on-searching-users')}`));
    },
    searchTracks() {
      searchTracks(this.localSearchTerm, this.tracksPagination)
        .then(({ data, pagination }) => {
          this.trackList = data;
          Object.assign(this.tracksPagination, pagination);
          if (this.trackList.length) this.checked = true;
          if (!this.trackList.length && !this.checked) {
            this.tabActive = 'questions';
            this.search();
          }
        })
        .catch(() => toastError(`${translate('error-on-searching-tracks')}`));
    },
    searchQuestions() {
      searchQuestions(this.localSearchTerm, this.questionsPagination)
        .then(({ data, pagination }) => {
          this.questionList = data;
          Object.assign(this.questionsPagination, pagination);
          if (this.questionList.length) this.checked = true;
          if (!this.questionList.length && !this.checked) {
            this.tabActive = 'articles';
            this.checked = true;
            this.search();
          }
        })
        .catch(() => toastError(`${translate('error-on-searching-questions')}`));
    },
    setDeleteUser(user) {
      this.deletedUser = user;
    },
    showModal() {
      return this.deletedUser;
    },
    onDeleteUser() {
      if (this.deletedUser) {
        removeUser(this.deletedUser).then(() => this.$router.go(0));
      }
    },
  },
};
</script>
